import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from "@angular/core";
import { Subscription } from "rxjs";
import { CampaignContentType } from "../../../shared/enums/campaign.enums";
import {
  CampaignPostContentType,
  LinkedInPostContentType,
} from "../../../shared/enums/campaignPost.enums";
import { BrandCampaign } from "../../../shared/models/brandCampaign";
import { BrandCampaignCategory } from "../../../shared/models/brandCampaignCategory";
import { BrandCampaignDetails } from "../../../shared/models/brandCampaignDetail";
import { BannerEntity } from "../../../shared/entities/file/banner.entity";
import { VideoEntity } from "../../../shared/entities/file/video.entity";
import { DialogService } from "../../../shared/services/dialog.service";
import { ImageService } from "../../../shared/services/image.service";
import { LanguageService } from "../../../shared/services/language.service";
import { NotificationService } from "../../../shared/services/notification.service";
import { RouteWithDataService } from "../../../shared/services/route-with-data.service";
import { BrandCampaignDialogService } from "../brand-campaign-dialog.service";
import { copyToClipboard } from "../../../shared/utils/copy-to-clipboard.interactor";
import { BrandCampaignService } from "../shared/services/brand-campaign.service";
import { CampaignAddContentDialogResult } from "./campaign-add-content-dialog/campaign-add-content-dialog.component";
import { CampaignContentFormVideoDialogData } from "./campaign-content-form-video-dialog/campaign-content-form-video-dialog.component";

@Component({
  selector: "app-brand-campaign-content",
  templateUrl: "./brand-campaign-content.component.html",
  styleUrl: "./brand-campaign-content.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class BrandCampaignContentComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChildren("navigationElement")
  private navigationElement!: QueryList<ElementRef>;
  @ViewChild("addCategoryBtn")
  private addCategoryBtn!: ElementRef<HTMLDivElement>;

  protected campaign: BrandCampaign;
  protected campaignDetails: BrandCampaignDetails;
  protected postsTabsSelectedIndex = 0;

  private readonly subscription = new Subscription();

  constructor(
    private readonly brandCampaignService: BrandCampaignService,
    private readonly imageService: ImageService,
    private readonly notificationService: NotificationService,
    private readonly routeWithDataService: RouteWithDataService,
    private readonly renderer: Renderer2,
    private readonly languageService: LanguageService,
    private readonly dialogService: DialogService,
    private readonly campaignDialogService: BrandCampaignDialogService,
  ) {
    this.campaign = this.brandCampaignService.currentBrandCampaign;
    this.campaignDetails = this.campaign.currentDetails;
  }

  public ngOnInit(): void {
    window.scrollTo(0, 0);
  }

  public ngAfterViewInit(): void {
    if (this.routeWithDataService.tabIndex) {
      this.postsTabsSelectedIndex = this.routeWithDataService.tabIndex;
    } else {
      this.postsTabsSelectedIndex = 0;
    }

    this.subscription.add(
      this.navigationElement.changes.subscribe((components) => {
        this.updateTabStyles(components);
      }),
    );

    this.navigationElement.notifyOnChanges();
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private updateTabStyles(components: any): void {
    const parentEl = components.first._elementRef.nativeElement;
    let buttonElement = this.addCategoryBtn.nativeElement;

    document.documentElement.style.setProperty(
      `--addCategoryBtnWidth`,
      `${buttonElement.offsetWidth + 50}px`,
    );

    this.subscription.add(
      this.languageService.changed$.subscribe(() => {
        setTimeout(() => {
          buttonElement = this.addCategoryBtn.nativeElement;
          document.documentElement.style.setProperty(
            `--addCategoryBtnWidth`,
            `${buttonElement.offsetWidth + 50}px`,
          );
        }, 100);
      }),
    );

    this.renderer.setStyle(buttonElement, "opacity", 1);
    this.renderer.insertBefore(parentEl, buttonElement, parentEl.childNodes[1]);
  }

  protected async onAddCategory(): Promise<void> {
    const result = await this.campaignDialogService.showContentCategoryForm({
      campaign: this.campaign,
      campaignDetails: this.campaignDetails,
    });

    if (result === "created") {
      if (this.campaignDetails.categories.length === 1) {
        this.postsTabsSelectedIndex = 1;
      }

      window.setTimeout(() => {
        this.postsTabsSelectedIndex =
          this.campaignDetails.categories.length - 1;
      }, 1000);
    }
  }

  protected onEditCategory(category: BrandCampaignCategory): void {
    this.campaignDialogService.showContentCategoryForm({
      campaign: this.campaign,
      campaignDetails: this.campaignDetails,
      category: category,
    });
  }

  protected async onAddContent(category: BrandCampaignCategory): Promise<void> {
    const result = await this.campaignDialogService.showBrandCampaignAddContent(
      {
        campaign: this.campaign,
        category: category,
      },
    );

    if (result) {
      await this.toCreateContent(result, category);
    }
  }

  private async toCreateContent(
    data: CampaignAddContentDialogResult,
    category: BrandCampaignCategory,
  ): Promise<void> {
    const creationData = {
      campaign: this.campaign,
      campaignDetails: this.campaignDetails,
      category: category,
    };

    switch (data.contentType) {
      case CampaignContentType.Post:
        switch (data.postContentType) {
          case CampaignPostContentType.Image:
          case CampaignPostContentType.Image360:
          case CampaignPostContentType.Gif:
          case CampaignPostContentType.ResponsiveImage:
            await this.campaignDialogService.showImagePostForm({
              ...creationData,
              postContentType: data.postContentType as CampaignPostContentType,
            });
            break;
          case CampaignPostContentType.Carousel:
            await this.campaignDialogService.showCarouselPostForm(creationData);
            break;
          case CampaignPostContentType.LinkPost:
            await this.campaignDialogService.showLinkPostForm(creationData);
            break;
          case CampaignPostContentType.Video:
            await this.campaignDialogService.showVideoPostForm(creationData);
            break;
        }
        break;
      case CampaignContentType.GoogleAd:
        await this.campaignDialogService.showGoogleAdsPostForm(creationData);
        break;
      case CampaignContentType.LinkedinPost:
        const linkedInData = {
          ...creationData,
          contentType: data.postContentType as LinkedInPostContentType,
        };

        switch (data.postContentType) {
          case LinkedInPostContentType.Image:
            await this.campaignDialogService.showLinkedInImagePostForm(
              linkedInData,
            );
            break;
          case LinkedInPostContentType.MultiImage:
            await this.campaignDialogService.showLinkedInMultiImagePostForm(
              linkedInData,
            );
            break;
          case LinkedInPostContentType.Video:
            await this.campaignDialogService.showLinkedInVideoPostForm(
              linkedInData,
            );
            break;
        }
        break;
      case CampaignContentType.Banner:
        await this.campaignDialogService.showBannerForm(creationData);
        break;
      case CampaignContentType.Video:
        await this.campaignDialogService.showVideoForm(creationData);
        break;
    }
  }

  protected onDeletePost(postId: number): void {
    this.campaignDetails.categories.forEach((category) => {
      category.posts = category.posts.filter((p) => p.id !== postId);
    });
  }

  protected async onEditBanner(
    category: BrandCampaignCategory,
    banner: BannerEntity,
  ): Promise<void> {
    const data = {
      campaign: this.campaign,
      campaignDetails: this.campaignDetails,
      category: category,
      banner: banner,
    };

    const updatedBanner = await this.campaignDialogService.showBannerForm(data);
    if (updatedBanner) {
      category.images = category.images.filter(
        (banner) => updatedBanner.id !== banner.id,
      );
      category.images.push(updatedBanner);
    }
  }

  protected async onEditVideo(
    category: BrandCampaignCategory,
    video: VideoEntity,
  ): Promise<void> {
    const data: CampaignContentFormVideoDialogData = {
      campaign: this.campaign,
      campaignDetails: this.campaignDetails,
      category: category,
      video: video,
    };

    const editedVideo = await this.campaignDialogService.showVideoForm(data);

    if (!editedVideo) {
      return;
    }

    category.videos = category.videos.map((video) =>
      editedVideo.id === video.id ? editedVideo : video,
    );
  }

  protected imageWideRelatedClass(
    img: HTMLImageElement,
    li: HTMLLIElement,
  ): void {
    li.style.maxWidth = img.naturalWidth + "px";

    if (img.clientWidth / img.clientHeight > 2) {
      li.classList.add("wide");
    }
  }

  protected getVideoImage(video: VideoEntity): string {
    if (video.image) {
      return video.image;
    }

    if (video.link) {
      const videoId = video.link.match(
        /(?:https?:\/{2})?(?:w{3}\.)?youtu(?:be)?\.(?:com|be)(?:\/watch\?v=|\/)([^\s&]+)/,
      );

      if (videoId) {
        return (
          "https://img.youtube.com/vi/" + videoId[1] + "/maxresdefault.jpg"
        );
      }
    }

    return "";
  }

  protected async copyLink(url: string): Promise<void> {
    await copyToClipboard(url);
    this.notificationService.info("shared.copiedToClipboard");
  }

  protected firstSectionMargin(category: BrandCampaignCategory): string {
    return (category.videos && category.videos.length > 0) ||
      (category.images && category.images.length > 0)
      ? "mb-big"
      : "";
  }

  protected secondSectionMargin(category: BrandCampaignCategory): string {
    return category.videos && category.videos.length > 0 ? "mb-big" : "";
  }

  protected onDeleteBannerConfirm(
    category: BrandCampaignCategory,
    banner: BannerEntity,
  ): void {
    this.dialogService.confirm({
      title: "brand.campaign.content.deleteBannerConfirmTitle",
      message: "brand.campaign.content.deleteBannerConfirm",
      onConfirm: () => this.onDeleteBanner(category, banner),
    });
  }

  private onDeleteBanner(
    category: BrandCampaignCategory,
    banner: BannerEntity,
  ): void {
    this.brandCampaignService
      .deleteCampaignDetailCategoryImage(
        this.campaign.id,
        this.campaignDetails.id,
        category.id,
        banner.id,
      )
      .subscribe({
        next: () => {
          category.images = category.images.filter((b) => b.id !== banner.id);
        },
        error: () => {
          this.notificationService.error("shared.errorSavingChanges");
        },
      });
  }

  protected onDeleteVideoConfirm(
    category: BrandCampaignCategory,
    video: VideoEntity,
  ): void {
    this.dialogService.confirm({
      title: "brand.campaign.content.deleteVideoConfirmTitle",
      message: "brand.campaign.content.deleteVideoConfirm",
      onConfirm: () => this.onDeleteVideo(category, video),
    });
  }

  private onDeleteVideo(
    category: BrandCampaignCategory,
    video: VideoEntity,
  ): void {
    this.brandCampaignService
      .deleteCampaignDetailCategoryVideo(
        this.campaign.id,
        this.campaignDetails.id,
        category.id,
        video.id,
      )
      .subscribe({
        next: () => {
          category.videos = category.videos.filter((i) => i.id !== video.id);
        },
        error: () => {
          this.notificationService.error("shared.errorSavingChanges");
        },
      });
  }

  protected optimizeImage(fileUrl: string, imagePublicId: string): string {
    return this.imageService.optimizeImage(fileUrl, imagePublicId);
  }

  protected getDownloadUrl(fileUrl: string, imagePublicId: string): string {
    return this.imageService.directDownloadURL(fileUrl, imagePublicId);
  }
}
